import { toNumber } from 'lodash';
import { SORT_ORDER } from '@common/types';
import { ALL_OPTION } from '@common/types/constants';
import dayjs from 'dayjs';
import {
  useAssessmentDatesQuery,
  useAssessmentTypesQuery,
} from '@common/cache';
import { useState } from 'react';

const completedOptions = [
  ALL_OPTION,
  {
    value: 'true',
    label: 'Completed',
  },
  {
    value: 'false',
    label: 'Incomplete',
  },
];

export type ColumnNames = 'type' | 'date';

export interface IAssessmentsTableEntry {
  id: string;
  name: string;
  type: string;
  date: string;
  completed: boolean;
  assessingProvider: {
    id: string;
    last_name: string | null;
    name: string;
    suffix?: string | null;
  } | null;
  leadingAnswers: {
    answer: string;
    question: string;
  }[];
  answerGroups: {
    groupName: string;
    answers: {
      answer: string;
      question: string;
    }[];
  }[];
}

export interface IAssessmentsTableFilters {
  sort_by: ColumnNames;
  sort_order: SORT_ORDER;
  page: number;
  limit: number;
  type?: string;
  completed?: string;
  date?: string;
}

interface IUseAssessmentsTableProps {
  filters: IAssessmentsTableFilters;
  setFilters: (newFilters: IAssessmentsTableFilters) => void;
  setItemEditId: (id: string) => void;
  patient_id: string;
}

export const useAssessmentsTable = ({
  filters,
  setFilters,
  patient_id,
  setItemEditId,
}: IUseAssessmentsTableProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerDetails, setDrawerDetails] =
    useState<IAssessmentsTableEntry | null>(null);
  const { data: types = [] } = useAssessmentTypesQuery();
  const { data: dates = [] } = useAssessmentDatesQuery({ patient_id });

  const assessmentTypes = types.map(entry => ({
    value: entry.id,
    label: entry.name,
  }));
  const assessmentTypeOptions = [ALL_OPTION, ...assessmentTypes];

  const assessmentDates = dates.map(entry => ({
    value: entry.date,
    label: dayjs(entry.date).format('MMM D, YYYY'),
  }));
  const assessmentDateOptions = [ALL_OPTION, ...assessmentDates];

  const { sort_order } = filters;

  const handleRowClick = (row: IAssessmentsTableEntry) => {
    if (!row.completed) return;
    setDrawerDetails(row);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setDrawerDetails(null);
  };

  const handleEditAssessment = (id: string) => {
    setIsDrawerOpen(false);
    setDrawerDetails(null);
    setItemEditId(id);
  };

  const handleChangeOrder = (colName: ColumnNames) => {
    setFilters({
      ...filters,
      sort_by: colName,
      sort_order: sort_order === 'asc' ? 'desc' : 'asc',
    });
  };

  const onPageIndexChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => {
    setFilters({ ...filters, page: page + 1 });
  };

  const onPageSizeChange = (event: {
    target: {
      value: string;
    };
  }) => {
    setFilters({ ...filters, limit: toNumber(event.target.value), page: 1 });
  };

  const handleSelectAssessmentType = (event: {
    target: {
      value: string;
    };
  }) => {
    setFilters({ ...filters, type: event.target.value });
  };

  const handleSelectCompletionStatus = (event: {
    target: {
      value: string;
    };
  }) => {
    setFilters({ ...filters, completed: event.target.value });
  };

  const handleSelectDate = (event: {
    target: {
      value: string;
    };
  }) => {
    setFilters({ ...filters, date: event.target.value });
  };

  return {
    handleSelectAssessmentType,
    handleSelectCompletionStatus,
    handleSelectDate,
    handleChangeOrder,
    onPageIndexChange,
    onPageSizeChange,
    completedOptions,
    assessmentTypeOptions,
    assessmentDateOptions,
    isDrawerOpen,
    drawerDetails,
    handleRowClick,
    handleCloseDrawer,
    handleEditAssessment,
  };
};
